'use client';

import { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import Intercom, {
  shutdown,
  show,
  update,
  trackEvent as intercomTrackEvent,
} from '@intercom/messenger-js-sdk';
import { useUser } from '@/app/context/user';

const APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID ?? '';

type IntercomContextValue = {
  trackEvent: (eventName: string, metadata?: Record<string, unknown>) => void;
  showMessenger: () => void;
};

const IntercomContext = createContext<IntercomContextValue>({
  trackEvent: () => {},
  showMessenger: () => {},
});

export function useIntercom() {
  return useContext(IntercomContext);
}

export default function IntercomProvider({ children }: { children: React.ReactNode }) {
  const { user, client } = useUser();
  const booted = useRef(false);
  const isInternalUser = user?.type === 'internal';

  useEffect(() => {
    if (!APP_ID || !user) return;

    if (isInternalUser) {
      if (booted.current) {
        shutdown();
        booted.current = false;
      }
      return;
    }

    const options = {
      app_id: APP_ID,
      user_id: user.id,
      email: user.email,
      name: `${user.first_name} ${user.last_name}`.trim(),
      hide_default_launcher: true,
      ...(client ? { company: { company_id: client.id, name: client.name } } : {}),
    };

    if (!booted.current) {
      Intercom(options);
      booted.current = true;
    } else {
      update(options);
    }
  }, [user, client, isInternalUser]);

  useEffect(() => {
    return () => {
      if (booted.current && APP_ID) {
        shutdown();
        booted.current = false;
      }
    };
  }, []);

  const trackEvent = useCallback(
    (eventName: string, metadata?: Record<string, unknown>) => {
      if (!APP_ID || !booted.current || isInternalUser) return;

      intercomTrackEvent(eventName, {
        user_id: user?.id,
        ...(client ? { client_id: client.id } : {}),
        ...metadata,
      });
    },
    [user, client, isInternalUser]
  );

  const showMessenger = useCallback(() => {
    if (!APP_ID || !booted.current || isInternalUser) return;
    show();
  }, [isInternalUser]);

  return (
    <IntercomContext.Provider value={{ trackEvent, showMessenger }}>
      {children}
    </IntercomContext.Provider>
  );
}
